import { useState } from "react";
import { evaluateFormula } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/constant.helper";
import { SECTION_TYPE_TABLE_SECTION } from "@Helpers/constants";
import type { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";
import { t } from "@i18n";
import {
  buildConcatenatedValue,
  formSectionType,
  getPermissions,
  TEMPLATE_SOURCE_MAIL_CREATION,
  TEMPLATE_SOURCE_MAIL_SIDEBAR,
  toFiniteNumber,
} from "./constants";
import type { SectionPermissions, TableSectionRow } from "./constants";
import type { FieldAnswer } from "./questionFieldsHelper";
import type { FormInstance } from "../../formInstance.wire";

/**
 * A section as these helpers read it. `Partial<FormTemplateField>` rather than the strict template
 * type because the same functions run against a live form instance, whose sections are looser
 * (`sectionHeading` is optional there), and against a half-built template in the form builder.
 * `permissions` exists only on instance sections, so it is spliced on separately.
 */
type ReadableSection = Partial<FormTemplateField> & { permissions?: SectionPermissions };

/** The form document as these helpers read it; only the captured template is ever touched. */
type ReadableFormData = { selectedForm?: { template?: Array<Partial<FormTemplateField>> } };

/** Arguments shared by the formula and concatenate resolvers. */
type CalculatedFieldArgs = {
  eachField?: Partial<QuestionField>;
  section?: ReadableSection;
  tableRowIndex?: number;
  formData?: ReadableFormData;
};

/**
 * Hint shown under formula/concatenate fields while their value is still
 * incomplete (evaluates to null). Shared by the web and mobile renderers.
 */
export const getCalculatedFieldHintText = () =>
  t("This value is calculated automatically once all required fields are filled");

/**
 * Build the numeric scope for a formula field.
 *
 * A number field contributes its raw numeric answer. A referenced *formula*
 * field no longer resolves to `null` — instead its own expression is evaluated
 * recursively (against its own `fieldsVariableMapper`) so formulas can reference
 * other formula fields. The `visiting` set guards against cyclic references: a
 * formula that (transitively) references itself resolves to `null` rather than
 * recursing forever.
 *
 * @param rootId          `_id` of the formula field being rendered; seeds the
 *                        cycle guard so a referenced formula pointing back at it
 *                        resolves to `null`. Accepts `undefined` because callers
 *                        read it off a partially built question.
 * @param mapper          `fieldsVariableMapper` of the formula being rendered
 *                        (referenced field `_id` → variable name).
 * @param questionById    All candidate questions keyed by `_id`.
 * @param getRawValue     Raw (pre-`toFiniteNumber`) answer value for a field id.
 */
export const buildFormulaScope = (
  rootId: string | undefined,
  mapper: Record<string, string>,
  questionById: Map<string, QuestionField>,
  getRawValue: (id: string) => unknown,
): Record<string, number> => {
  const resolveValue = (question: QuestionField, visiting: Set<string | undefined>): number | null => {
    if (question.fieldType === "formula") {
      if (visiting.has(question._id)) return null;
      visiting.add(question._id);
      const nestedScope: Record<string, number> = {};
      Object.entries(question.fieldsVariableMapper ?? {}).forEach(([refId, varName]) => {
        const refQuestion = questionById.get(refId);
        if (!refQuestion || !varName) return;
        const value = resolveValue(refQuestion, visiting);
        if (value !== null) nestedScope[varName] = value;
      });
      visiting.delete(question._id);
      return evaluateFormula(question.formulaExpression || "", nestedScope);
    }
    return toFiniteNumber(getRawValue(question._id));
  };

  const scope: Record<string, number> = {};
  Object.entries(mapper).forEach(([refId, varName]) => {
    const question = questionById.get(refId);
    if (!question || !varName) return;
    const value = resolveValue(question, new Set([rootId]));
    if (value !== null) scope[varName] = value;
  });
  return scope;
};

export const collectNonTableQuestions = (template: Array<Partial<FormTemplateField>> = []): QuestionField[] =>
  template.flatMap((section) =>
    section.sectionCode === SECTION_TYPE_TABLE_SECTION ? [] : (section.questionFields ?? []),
  );

export const questionMapById = (questions: QuestionField[]): Map<string, QuestionField> =>
  new Map(questions.map((question) => [question._id, question]));

// Builds the numeric scope for a formula field (row-scoped for table sections,
// form-wide otherwise) and evaluates its expression. Referenced formula fields
// resolve recursively via buildFormulaScope. Returns null while any referenced
// field is still empty/non-numeric.
export const getFormulaFieldValue = ({
  eachField = {},
  section = {},
  tableRowIndex = 0,
  formData = {},
}: CalculatedFieldArgs) => {
  const fieldVariableMapper = eachField.fieldsVariableMapper ?? {};
  const formulaExpression = eachField.formulaExpression || "";

  let scope: Record<string, number>;
  if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) {
    // Annotated rather than left as the schema's `z.array(z.any())` element, so the `?.value` read
    // below resolves to `unknown` instead of `any`. Annotation only — nothing is emitted.
    const rowData: TableSectionRow = section.tableDataSource?.[tableRowIndex] ?? {};
    const questionById = questionMapById(section.questionFields ?? []);
    scope = buildFormulaScope(eachField._id, fieldVariableMapper, questionById, (id) => rowData?.[id]?.value);
  } else {
    const questionById = questionMapById(collectNonTableQuestions(formData?.selectedForm?.template));
    scope = buildFormulaScope(
      eachField._id,
      fieldVariableMapper,
      questionById,
      (id) => questionById.get(id)?.answer?.value,
    );
  }

  return evaluateFormula(formulaExpression, scope);
};

// Resolves the values of the fields referenced by a concatenate field
// (row-scoped for table sections, form-wide otherwise) and joins them with the
// configured separator. Returns null while nothing resolves.
export const getConcatenateFieldValue = ({
  eachField = {},
  section = {},
  tableRowIndex = 0,
  formData = {},
}: CalculatedFieldArgs) => {
  const valueById = new Map<string, string>();

  if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) {
    const rowData: TableSectionRow = section.tableDataSource?.[tableRowIndex] ?? {};
    Object.keys(rowData).forEach((key: string) => {
      // The `as string` casts here and below are erased. An answer `value` is `z.unknown()` on the
      // wire; a concatenate field only ever joins text-shaped answers, and the `?? ""` that was
      // already here is what covers the absent case.
      valueById.set(key, (rowData[key]?.value as string) ?? "");
    });
  } else {
    (formData?.selectedForm?.template ?? []).forEach((templateSection) => {
      if (templateSection.sectionCode === SECTION_TYPE_TABLE_SECTION) return;
      (templateSection.questionFields ?? []).forEach((question) => {
        valueById.set(question._id, (question?.answer?.value as string) ?? "");
      });
    });
  }

  return buildConcatenatedValue(eachField.selectedFields, valueById, eachField.seperator) || null;
};

type UseQuestionFieldsArgs = {
  eachField?: Partial<QuestionField>;
  tableRowId?: string | number | null;
  extraParams?: Record<string, unknown>;
  section?: ReadableSection;
  tableRowIndex?: number;
  source?: string;
  formData?: Partial<FormInstance>;
  userId?: string;
  disabled?: boolean;
  showAnswersOnly?: boolean;
  templateSource?: string;
};

function useQuestionFields({
  eachField = {},
  tableRowId = "",
  extraParams = {},
  section = {},
  tableRowIndex = 0,
  source = "",
  formData = {},
  userId = "",
  disabled = false,
  showAnswersOnly = false,
  templateSource = "FORM_FILLING",
}: UseQuestionFieldsArgs) {
  const questionId = eachField._id ?? "";

  // `uniqueId` is declared even though the seed below omits it: `questionFields` both writes it
  // (`setOpenUrlPopUpState({ state: true, url, uniqueId: name })`) and reads it back to decide which
  // field's popup is open. Inference from the seed alone would leave it off the type and reject both.
  // Type argument only — the seed value is unchanged, so nothing is emitted differently.
  const [openUrlPopUpState, setOpenUrlPopUpState] = useState<{ state: boolean; url: string; uniqueId?: string }>({
    state: false,
    url: "",
  });
  let name = tableRowId ? `${tableRowId}${questionId}` : questionId;
  if (extraParams.id !== undefined && extraParams.id !== null) {
    name = `${extraParams.id}_${name}`;
  }

  const sectionId = section._id ?? "";

  const { permissions, tableDataSource = [] } = section;

  // Annotated so the table-row branch — whose rows are `z.array(z.any())` on the wire — does not
  // turn the answer back into `any` for every consumer of this hook. Annotation only.
  let currentAnswer: FieldAnswer<unknown> = eachField.answer ?? {};
  if (source === formSectionType.TABLE_SECTION) {
    currentAnswer = tableDataSource[tableRowIndex]?.[questionId] ?? {};
  }
  const extraCheck = eachField.allowPredefinedVal || eachField.allowPreDefinedColValues;
  const allowEditDuringFormFilling =
    !extraCheck || (extraCheck && (eachField.allowEditingOfPredefinedVal || section.allowEditPreDefineRowInForm));

  const isCurrentUserAllowedToFill = permissions
    ? getPermissions({ inspectionData: formData, permissions, type: "Fill", userId })
    : !disabled || templateSource === "TEMPLATE_CREATION";

  const isCurrentUserAllowedToModify = permissions
    ? getPermissions({ inspectionData: formData, permissions, type: "Modify", userId })
    : !disabled || templateSource === "TEMPLATE_CREATION";

  const enabled =
    (isCurrentUserAllowedToFill || isCurrentUserAllowedToModify) &&
    !showAnswersOnly &&
    ((["FORM_FILLING", TEMPLATE_SOURCE_MAIL_CREATION, TEMPLATE_SOURCE_MAIL_SIDEBAR].includes(templateSource) &&
      !!allowEditDuringFormFilling) ||
      (templateSource === "TEMPLATE_CREATION" && !!extraCheck));

  const formulaValue =
    eachField.fieldType === "formula" ? getFormulaFieldValue({ eachField, section, tableRowIndex, formData }) : null;

  const concatenatedValue =
    eachField.fieldType === "concatenate"
      ? getConcatenateFieldValue({ eachField, section, tableRowIndex, formData })
      : null;

  const autoNumberValue = currentAnswer?.value ?? "";

  return {
    questionId,
    name,
    sectionId,
    currentAnswer,
    isCurrentUserAllowedToFill,
    isCurrentUserAllowedToModify,
    enabled,
    permissions,
    setOpenUrlPopUpState,
    openUrlPopUpState,
    formulaValue,
    concatenatedValue,
    autoNumberValue,
  };
}

export default useQuestionFields;
